'use client';

import { useState } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { Loader2 } from 'lucide-react';
import { Button } from '@/shared/ui/shadcn/button';
import { useNestedSheetStack } from '@/shared/ui';
import { useProject } from '@/features/project/hooks/use-projects';
import { ProjectGoalsSheet } from '@/features/project/ui/ProjectGoalsSheet';
import { goalsAutosaveCoordinator } from '@/features/project/lib/goals-autosave-coordinator';
import { useEstimateActions } from '../hooks/use-estimate';
import { useConceptSelectedRenders } from '../hooks/use-concept-selected-renders';
import { useEstimateDraftStore } from '../model/estimate-draft.store';
import { EstimateSettingsList } from './EstimateHeader';
import type { EstimateIntroAttributes } from '../hooks/use-estimate-intro';

interface EstimateDraftSettingsProps {
  projectId: string;
  conceptId: string;
  /** Server-driven intro payload — seeds the draft defaults (currency/units). */
  intro: EstimateIntroAttributes;
  /**
   * 'panel': two-column inspector hosting — settings scroll, Generate pinned
   * to the column footer (Regenerate-footer parity), hidden while a drill-down
   * sub-panel owns the column. 'inline': stacked flow — Generate after the
   * settings group.
   */
  variant: 'inline' | 'panel';
}

/**
 * Working pre-generation settings for the Estimate tab's empty state
 * (entitled users only — the non-entitled intro keeps the marketing bullets +
 * paywall CTA). Reuses the completed-estimate settings list in DRAFT mode: no
 * estimate row exists yet, so Language / Special requirements flip an
 * in-memory per-concept draft (estimate-draft.store) instead of PATCHing, and
 * Generate POSTs the draft along with the selected renders. Currency / units
 * are NOT picked pre-generation anymore: the generation derives them from the
 * LLM's display recommendation, and the title tags switch them afterwards.
 * Region / Facade area stay live project values edited through the shared
 * project-goals drill-down — closing it flushes any pending PATCH, which
 * refreshes the intro + project queries (goalsAutosaveCoordinator).
 */
export function EstimateDraftSettings({
  projectId,
  conceptId,
  intro,
  variant,
}: EstimateDraftSettingsProps) {
  const t = useTranslations('common.estimate');
  const locale = useLocale();

  // Drill-down chrome parity with the completed-estimate inspector
  // (EstimateSettingsPanel): while a sub-panel (goals / currency / language /
  // special-requirements NestedSheet) is open it owns the column, so the
  // pinned Generate footer must not paint under/over it.
  const { hasOpenNestedSheet } = useNestedSheetStack();

  const draft = useEstimateDraftStore((s) => s.drafts[conceptId]);
  const patchDraft = useEstimateDraftStore((s) => s.patchDraft);
  const clearDraft = useEstimateDraftStore((s) => s.clearDraft);

  // Live project values for the Region / Facade-area rows — the same
  // project-derived display the goals editor shows (area display-converted
  // server-side into `facade_area_unit`).
  const { data: projectResponse } = useProject(projectId);
  const project = projectResponse?.data;

  const { createEstimate, isCreating } = useEstimateActions(
    projectId,
    conceptId
  );
  const selectedRenders = useConceptSelectedRenders(conceptId);

  // Effective draft values: only touched fields live in the store; the rest
  // fall back to the app locale — the exact value the API applies when the
  // POST omits it.
  const language = draft?.language ?? locale;
  const specialRequirements = draft?.special_requirements ?? null;

  // Region / Facade-area drill-down — the shared project-goals editor. On
  // close, flush any pending goals PATCH: the coordinator's flush invalidates
  // the project + estimate-intro queries, so the rows refresh.
  const [showGoalsSheet, setShowGoalsSheet] = useState(false);
  const handleGoalsSheetChange = (open: boolean) => {
    setShowGoalsSheet(open);
    if (!open) {
      void goalsAutosaveCoordinator.flushAndAwait(projectId);
    }
  };

  const handleGenerate = () => {
    if (isCreating) return;
    const trimmedRequirements = specialRequirements?.trim() ?? '';
    createEstimate(
      {
        selected_renders: selectedRenders,
        language,
        // Currency / measurement system are deliberately omitted: the server
        // seeds country-derived defaults and the generation re-derives them
        // from the LLM's display recommendation (an explicit value here would
        // pin them and disable that).
        // Blank requirements are omitted — the field is optional and the API
        // treats absence as "none".
        ...(trimmedRequirements !== ''
          ? { special_requirements: trimmedRequirements }
          : {}),
      },
      {
        // The created estimate owns the values now — a stale draft would
        // resurface outdated choices if this estimate is later deleted.
        onSuccess: () => clearDraft(conceptId),
      }
    );
  };

  const settingsList = (
    <EstimateSettingsList
      values={{
        regionLabel: intro.region_label,
        facadeArea: project?.attributes.facade_area ?? null,
        facadeAreaUnit: project?.attributes.facade_area_unit ?? 'm2',
        language,
        specialRequirements,
      }}
      disabled={isCreating}
      onLanguageChange={(value) => patchDraft(conceptId, { language: value })}
      onOpenProjectGoals={() => handleGoalsSheetChange(true)}
      onSpecialRequirementsSave={(value) =>
        patchDraft(conceptId, { special_requirements: value ?? '' })
      }
    />
  );

  const generateFooter = (
    // Stays ACTIVE with no completed renders: the tap fires and the server's
    // localized `requires_completed_render` message surfaces as an error toast
    // (use-estimate onError; API-driven messages — same contract on
    // Apple/Android).
    <div className="flex justify-center">
      <Button
        variant="teal"
        size="cta"
        onClick={handleGenerate}
        disabled={isCreating}
      >
        {isCreating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
        {t('generate')}
      </Button>
    </div>
  );

  // Shared project-goals editor (goals + region + facade area) — opened by
  // the Region / Facade-area rows.
  const goalsSheet = (
    <ProjectGoalsSheet
      projectId={projectId}
      open={showGoalsSheet}
      onOpenChange={handleGoalsSheetChange}
    />
  );

  if (variant === 'panel') {
    return (
      /* `relative` bounds the drill-down NestedSheets to the inspector column —
         completed-estimate inspector parity (EstimateSettingsPanel). */
      <div className="relative flex h-full flex-col">
        <div className="min-h-0 flex-1 overflow-y-auto pb-2">
          {settingsList}
        </div>

        {/* Generate CTA pinned to the inspector footer (Regenerate-footer
            parity on the completed estimate). Hidden while a drill-down
            sub-panel is open — the NestedSheet owns the whole column. */}
        {!hasOpenNestedSheet && (
          <div className="flex flex-shrink-0 flex-col gap-3 pt-3 pb-4">
            {generateFooter}
          </div>
        )}

        {goalsSheet}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {settingsList}
      {generateFooter}
      {goalsSheet}
    </div>
  );
}
